import {inject, injectable} from 'inversify'
import {action, computed, makeObservable, observable, reaction, runInAction} from 'mobx'
import keys from 'lodash/keys'
import values from 'lodash/values'

import {DI} from '@/src/config/di/types'
import {
  AddCertificateUseCase,
  AddToBasketUseCase,
  RemoveFromBasketUseCase,
  SyncBasketUseCase,
  UpdateBasketItemUseCase,
} from '@/src/core/application/basket'
import type {INotificationService} from '@/src/ports/services/notification'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import type {Product, ProductSimple, ProductVariant} from '@/src/core/domain/entities/product'
import type {
  AddEventSubscription,
  BasketData,
  BasketEvent,
  BasketItem,
  NewBasketItem,
  SyncBasketItem,
} from '@/src/entities/basket/types'
import {PageType} from '@/src/core/domain/entities/page'
import {getPageFetchKey} from '@/src/shared/utils/page-fetch-key'
import type {QueryCacheInterface} from '@/src/ports/services/query-cache'
import type {IEventBus} from '@/src/core/domain/events'
import {BasketEvents} from '@/src/core/domain/events'
import {BasketStoreInterface} from '@/src/presentation/stores/basket/basket.store.interface'
import {ProductBuyEvent, ProductDetailEventTypes} from '@/src/core/domain/events/product.events'
import {Routes} from '@/src/config/routes'
import {settings} from '@/src/config/settings'
import type {ILoggerService} from '@/src/ports/services/logger'

const DEBOUNCE_DELAY = 700

/**
 * BasketStore - Presentation Layer
 *
 * Responsible for:
 * - UI state management (basketData, pending, modifyItems)
 * - Event dispatching and subscription
 * - Delegating business operations to use cases
 * - Handling debounced quantity updates
 *
 * This store follows SRP: it only manages UI state and orchestrates use cases.
 * Business logic (validation, API calls) is handled by use cases.
 */
@injectable()
export class BasketStore implements BasketStoreInterface {
  static key = 'basket'

  @observable
  modifyItems: {[key: string]: SyncBasketItem} = {}

  @observable
  basketData: Nullable<BasketData> = null

  @observable
  events: BasketEvent[] = []

  @observable
  changeCbs: AddEventSubscription[] = []

  @observable
  pendingTimeout: Nullable<ReturnType<typeof setTimeout>> = null

  @observable
  pending: boolean = false

  @observable
  loading = true

  @observable
  loadingSwitched = false

  @observable
  pageType!: ValuesOf<typeof PageType>

  constructor(
    @inject(DI.AddToBasketUseCase) readonly addToBasketUseCase: AddToBasketUseCase,
    @inject(DI.AddCertificateUseCase) readonly addCertificateUseCase: AddCertificateUseCase,
    @inject(DI.UpdateBasketItemUseCase) readonly updateBasketItemUseCase: UpdateBasketItemUseCase,
    @inject(DI.RemoveFromBasketUseCase) readonly removeFromBasketUseCase: RemoveFromBasketUseCase,
    @inject(DI.SyncBasketUseCase) readonly syncBasketUseCase: SyncBasketUseCase,
    @inject(DI.INotificationService) readonly notifications: INotificationService,
    @inject(DI.IErrorReporter) readonly errorReporter: IErrorReporter,
    @inject(DI.IQueryCacheService) readonly queryCache: QueryCacheInterface,
    @inject(DI.IEventBus) readonly eventBus: IEventBus,
    @inject(DI.ILoggerService) readonly logger: ILoggerService,
  ) {
    makeObservable(this)
    this.setupEventReaction()
    this.setupBasketDataReaction()
    this.setupLoadingReaction()
  }

  private setupEventReaction() {
    this.eventBus.subscribe(ProductDetailEventTypes.BuyProduct, async (event: ProductBuyEvent) => {
      const eventId = event.eventId

      if (!event.payload) {
        return
      }

      await this.eventBus.publish(BasketEvents.startProcess({eventId}))
      try {
        await this.addProduct(event.payload?.product, event.payload?.variant)
      } catch (e: unknown) {
        this.notifications.error('Ошибка при добавлении товара в корзину')
        this.errorReporter.capture(e)
      } finally {
        await this.eventBus.publish(BasketEvents.finishProcess({eventId}))
      }
    })

    this.onAdd(async basketEvent => {
      if (basketEvent.event === 'error') {
        const error =
          basketEvent.data instanceof Error ? basketEvent.data : new Error(String(basketEvent.data))

        this.notifications.error(error.message)
        this.errorReporter.capture(error)
      } else {
        if (basketEvent.event === 'add') {
          this.notifications.success('Товар успешно добавлен в корзину')
        }

        if (['increased', 'decreased', 'deleted'].includes(basketEvent.event)) {
          this.notifications.success('Корзина успешно изменена')
        }

        if (this.pageType === PageType.Cart) {
          await this.queryCache.invalidateQueries([getPageFetchKey(this.pageType)]).then(() => {
            runInAction(() => {
              this.modifyItems = {}
            })
          })
          await this.queryCache.invalidateQueries(['layout'])
        }
      }
    })

    reaction(
      () => this.events,
      async events => {
        if (events.length > 0) {
          for (const fn of this.changeCbs) {
            for (const event of events) {
              await fn(event)
            }
          }
          runInAction(() => {
            this.clearEvents()
          })
        }
      },
    )
  }

  private setupBasketDataReaction() {
    reaction(
      () => this.basketData,
      basketData => {
        const layoutData = this.queryCache.getQueryData<LayoutData>(['layout'])
        if (layoutData) {
          this.queryCache.setQueryData(['layout'], {
            ...layoutData,
            header: {...layoutData.header, cart: basketData},
          })
        }
      },
    )
  }

  private setupLoadingReaction() {
    this.queryCache.subscribeToCache(() => {
      const fetching = this.queryCache.isFetching(['layout'])

      if (this.loadingSwitched && fetching !== this.loading) {
        setTimeout(() => {
          runInAction(() => {
            this.loading = fetching
          })
        }, 500)
      } else if (!this.loadingSwitched && fetching) {
        runInAction(() => {
          this.loadingSwitched = true
        })
      }
    })
  }

  @computed
  get haveNotAvailable() {
    return values(this.basketData?.lines ?? {}).some(basketItem => !basketItem.available)
  }

  @computed
  get lines() {
    return this.basketData?.lines ?? {}
  }

  @computed
  get count() {
    return keys(this.basketData?.lines ?? {}).length
  }

  @computed
  get total() {
    return this.basketData?.discount_total_formatted ?? '-'
  }

  get haveProduct() {
    return (product: Product, variant?: ProductVariant) => {
      if (!this.basketData?.lines) return false

      return [product.id, variant?.id].filter(x => !!x).join('_') in this.basketData.lines
    }
  }

  @action
  setPageType(pageType: ValuesOf<typeof PageType>) {
    this.pageType = pageType
  }

  @action
  setBasketData(basketData: Nullable<BasketData>) {
    this.basketData = basketData
  }

  @action.bound
  dispatchEvent(event: BasketEvent) {
    this.events.push(event)
    this.events = [...this.events]
  }

  @action.bound
  clearEvents() {
    this.events = []
  }

  @action.bound
  onAdd(subscription: AddEventSubscription) {
    this.changeCbs.push(subscription)
  }

  @action.bound
  setPending(pending: boolean) {
    this.pending = pending
  }

  @action
  setLoading(loading: boolean) {
    this.loading = loading
  }

  /**
   * Add certificate product to basket
   * Delegates to AddCertificateUseCase
   */
  @action.bound
  async addCertificate(certificateId: ProductSimple['id'], amount?: number) {
    try {
      this.setPending(true)

      const result = await this.addCertificateUseCase.execute(certificateId, amount)

      runInAction(() => {
        if (result.isOk()) {
          this.setBasketData(result.value)

          this.dispatchEvent({
            event: 'add',
            data: {
              product_id: certificateId,
              quantity: 1,
              amount: amount ?? null,
            } satisfies NewBasketItem,
          })

          // Publish domain event
          this.eventBus.publish(
            BasketEvents.certificateAdded({
              productId: certificateId,
              amount: amount ?? 0,
            }),
          )
        } else {
          this.dispatchEvent({
            event: 'error',
            data: 'Не удалось добавить сертификат в корзину',
          })
        }
      })
    } catch (e: unknown) {
      this.dispatchEvent({
        event: 'error',
        data: e,
      })
    } finally {
      this.setPending(false)
    }
  }

  /**
   * Add product to basket
   * Delegates to AddToBasketUseCase
   */
  @action.bound
  async addProduct(product: Product, variant?: ProductVariant) {
    try {
      this.setPending(true)

      const result = await this.addToBasketUseCase.execute(product, variant)

      runInAction(() => {
        try {
          if (result.isOk()) {
            this.setBasketData(result.value)
            this.dispatchEvent({
              event: 'add',
              data: {
                product_id: product.id,
                quantity: 1,
                variant_id: variant?.id,
              } satisfies NewBasketItem,
            })

            const productIsSimple = (product: Product): product is ProductSimple =>
              (['simple', 'certificate'] as Product['type'][]).includes(product.type)

            const price =
              // Если товар с предложениями
              variant?.price.price ||
              // Иначе если обычный товар
              (productIsSimple(product) && product.price?.price) ||
              0

            this.eventBus.publish(
              BasketEvents.productAdded({
                productId: product.id,
                productName: variant?.name ?? product.name,
                imageUrl:
                  settings.NEXT_PUBLIC_FRONTEND_URL +
                    (product.images && product.images.length > 0 && product.images[0]!) || '',
                productUrl:
                  settings.NEXT_PUBLIC_FRONTEND_URL +
                  Routes.catalog.product.page(product.code, product.type),
                price,
                variantId: variant?.id,
                quantity: 1,
              }),
            )
          } else {
            this.dispatchEvent({
              event: 'error',
              data: 'Не удалось добавить товар в корзину',
            })
          }
        } catch (e: unknown) {
          console.error(e)
        }
      })
    } catch (e: unknown) {
      this.dispatchEvent({
        event: 'error',
        data: e,
      })
    } finally {
      this.setPending(false)
    }
  }

  quantityIsAlwaysAvailable(basketItem: BasketItem) {
    return basketItem.availability <= 0 && basketItem.available
  }

  getItemQuantity(basketItem: BasketItem) {
    return basketItem.id in this.modifyItems
      ? this.modifyItems[basketItem.id]!.quantity
      : basketItem.quantity
  }

  getModifiedSyncBasketItem(basketItem: BasketItem, quantity: number): SyncBasketItem {
    return basketItem.id in this.modifyItems
      ? {...this.modifyItems[basketItem.id]!, quantity}
      : {
          id: basketItem.id,
          product_id: basketItem.product_id,
          variant_id: basketItem.variant_id,
          quantity,
          amount: basketItem.is_custom_price ? basketItem.price : null,
        }
  }

  /**
   * Increase item quantity with debounce
   * Uses UpdateBasketItemUseCase for validation
   */
  @action.bound
  async increaseCount(basketItem: BasketItem) {
    const quantity = this.getItemQuantity(basketItem)

    if (!this.quantityIsAlwaysAvailable(basketItem)) {
      if (quantity >= basketItem.availability) {
        this.notifications.warning(
          `Доступно только ${basketItem.availability} шт., больше добавить нельзя`,
        )
        return
      }
    }

    const syncBasketItem = this.getModifiedSyncBasketItem(basketItem, quantity + 1)

    runInAction(() => {
      this.modifyItems[syncBasketItem.id] = syncBasketItem
    })

    this.logger.info('Пользователь увеличил количество товара в корзине', {basketItem})

    this.debouncedSync('increased')
  }

  /**
   * Decrease item quantity with debounce
   * Uses UpdateBasketItemUseCase for validation
   */
  @action.bound
  async decreaseCount(basketItem: BasketItem) {
    const quantity = this.getItemQuantity(basketItem)

    if (quantity <= 0) {
      return
    }

    const syncBasketItem = this.getModifiedSyncBasketItem(basketItem, quantity - 1)

    runInAction(() => {
      this.modifyItems[syncBasketItem.id] = syncBasketItem
    })

    this.logger.info('Пользователь убавил количество товара в корзине', {basketItem})

    this.debouncedSync('decreased')
  }

  /**
   * Debounced sync to avoid excessive API calls
   * Delegates to SyncBasketUseCase
   */
  @action.bound
  private debouncedSync(eventType: 'increased' | 'decreased') {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout)
    }

    this.pendingTimeout = setTimeout(async () => {
      this.setPending(true)

      try {
        const items = values(this.modifyItems)
        const result = await this.syncBasketUseCase.execute(items)

        runInAction(() => {
          if (result.isOk()) {
            this.setBasketData(result.value)
            this.dispatchEvent({event: eventType})

            // Publish domain event for synced basket
            this.eventBus.publish(
              BasketEvents.synced({
                itemCount: keys(result.value.lines ?? {}).length,
                totalAmount: result.value.discount_total ?? 0,
              }),
            )
          } else {
            this.dispatchEvent({
              event: 'error',
              data: 'Что-то пошло не так',
            })
          }
          this.modifyItems = {}
        })
      } catch (e: unknown) {
        runInAction(() => {
          this.modifyItems = {}
        })
        this.dispatchEvent({
          event: 'error',
          data: e,
        })
      } finally {
        this.setPending(false)
      }
    }, DEBOUNCE_DELAY)
  }

  /**
   * Delete item from basket
   * Delegates to RemoveFromBasketUseCase
   */
  @action.bound
  async deleteBasketItem(basketItem: BasketItem) {
    try {
      this.setPending(true)

      const result = await this.removeFromBasketUseCase.execute(basketItem)

      runInAction(() => {
        if (result.isOk()) {
          this.logger.info('Пользователь удалил товар из корзины', {basketItem})
          this.setBasketData(result.value)
          this.dispatchEvent({event: 'deleted'})

          // Publish domain event
          this.eventBus.publish(
            BasketEvents.productRemoved({
              basketItemId: basketItem.id,
              productId: basketItem.product_id,
              variantId: basketItem.variant_id,
              productName: basketItem.name ?? basketItem.product?.name ?? '<?>',
            }),
          )
        } else {
          this.logger.error('Не удалось удалить товар из корзины', result.error, {basketItem})
          this.modifyItems = {}
          this.dispatchEvent({
            event: 'error',
            data: 'Что-то пошло не так',
          })
        }
      })
    } catch (e: unknown) {
      runInAction(() => {
        this.modifyItems = {}
      })
      this.dispatchEvent({
        event: 'error',
        data: e,
      })
    } finally {
      this.setPending(false)
    }
  }
}
